import React, { Component } from "react";
import moment from "moment";
import Bridge from "../../utils/Bridge";
import { Spin, message } from "antd";
import "./PrintReport.css";

export default class PrintReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      respiratoryData: [],
      mptData: [],
      alsfrsrData: [],
      speechData: [],
      eals_id: this.props.eals_id,
    };
  }

  componentDidMount() {
    this.fetchAllData();
  }

  fetchAllData = async () => {
    this.setState({ loading: true });
    try {
      // Fetch all data in parallel
      await Promise.all([
        this.fetchRespiratoryData(),
        this.fetchMPTData(),
        this.fetchALSFRSRData(),
        this.fetchSpeechData(),
      ]);
      this.setState({ loading: false });
    } catch (error) {
      console.error("Error in fetchAllData:", error);
      const lang = this.props.formData?.page?.printReport || {};
      message.error(lang.failedToLoadReportData || "Failed to load report data");
      this.setState({ loading: false, speechData: [] });
    }
  };

  fetchRespiratoryData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_in_clinic_session_list(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ respiratoryData: response.data });
        }
      })
      .catch((error) => {
        console.error("Error fetching respiratory data:", error);
      });
  };

  fetchMPTData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_in_clinic_session_list(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ mptData: response.data });
        }
      })
      .catch((error) => {
        console.error("Error fetching MPT data:", error);
      });
  };

  fetchALSFRSRData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_eals_frs_score(params)
      .then((response) => {
        if (response.status == "1") {
          this.setState({ alsfrsrData: response.data });
        }
      })
      .catch((error) => {
        console.error("Error fetching ALSFRS-R data:", error);
      });
  };

  fetchSpeechData = () => {
    let params = "?eals_id=" + this.state.eals_id;
    return Bridge.personal_data_viewer.le_study
      .get_le_study_session_list(params)
      .then((response) => {
        if (response.status == "1" && response.data && response.data.length > 0) {
          this.setState({ speechData: response.data });
        } else {
          this.setState({ speechData: [] });
        }
      })
      .catch((error) => {
        console.error("Error fetching speech data:", error);
        this.setState({ speechData: [] });
      });
  };

  handlePrint = () => {
    window.print();
  };

  render() {
    const { loading, respiratoryData, mptData, alsfrsrData, speechData } =
      this.state;
    const { formData, onClose } = this.props;
    const lang = formData?.page?.printReport || {};
    const hasAnyData =
      respiratoryData.length > 0 ||
      mptData.length > 0 ||
      alsfrsrData.length > 0 ||
      speechData.length > 0;

    // Debug log
    console.log(
      "PrintReport render - speechData:",
      speechData,
      "length:",
      speechData.length,
      
    );

    return (
      <div className="print-report-container">
        <Spin spinning={loading}>
          <div className="print-header no-print">
            <h2>
              {lang.title || "My Health Report"}
            </h2>
            <div className="print-actions">
              <button
                onClick={this.handlePrint}
                className="print-button"
                disabled={!hasAnyData}
              >
                {lang.printSaveAsPdf || "Print / Save as PDF"}
              </button>
              <button onClick={onClose} className="close-button">
                {lang.close || "Close"}
              </button>
            </div>
          </div>

          {!hasAnyData && !loading && (
            <div
              className="no-data-message"
              style={{ padding: "40px", textAlign: "center" }}
            >
              <h3>{lang.noDataAvailable || "No Data Available"}</h3>
              <p>
                {lang.noDataMessage || "You don't have any session data yet. Complete your first session to see your health report."}
              </p>
            </div>
          )}

          {hasAnyData && (
            <div className="print-content">
              {/* Header for printed page */}
              <div className="print-only print-page-header">
                <h1>{lang.personalHealthReport || "Personal Health Report"}</h1>
                <p>{lang.generatedOn || "Generated on:"} {moment().format("MMMM DD, YYYY")}</p>
                <hr />
              </div>

              {/* Session Overview */}
              <section className="report-section">
                <h2 className="section-title">{lang.sessionOverview || "Session Overview"}</h2>
                <div className="section-content session-overview-box">
                  <div style={{ display: "flex", gap: "20px" }}>
                    {/* Column 1: In-Clinic Sessions */}
                    <div style={{ flex: 1 }}>
                      <p>
                        <strong>{lang.totalInClinicSessions || "Total In-Clinic Sessions:"}</strong>{" "}
                        {respiratoryData.length || 0}
                      </p>
                      {respiratoryData.length > 0 ? (
                        <>
                          <p>
                            <strong>{lang.firstSession || "First Session:"}</strong>{" "}
                            {moment(
                              respiratoryData[respiratoryData.length - 1]
                                ?.entry_date
                            ).format("MM/DD/YYYY")}
                          </p>
                          <p>
                            <strong>{lang.latestSession || "Latest Session:"}</strong>{" "}
                            {moment(respiratoryData[0]?.entry_date).format(
                              "MM/DD/YYYY"
                            )}
                          </p>
                        </>
                      ) : (
                        <p style={{ color: "#999", fontStyle: "italic" }}>
                          {lang.noSessionDataAvailable || "No session data available yet."}
                        </p>
                      )}
                    </div>

                    {/* Column 2: ALSFRS-R Assessments */}
                    <div style={{ flex: 1 }}>
                      {alsfrsrData.length > 0 ? (
                        <>
                          <p>
                            <strong>{lang.totalALSFRSRAssessments || "Total ALSFRS-R Assessments:"}</strong>{" "}
                            {alsfrsrData.length}
                          </p>
                          <p>
                            <strong>{lang.latestALSFRSRAssessment || "Latest ALSFRS-R Assessment:"}</strong>{" "}
                            {moment(alsfrsrData[0]?.created_at).format(
                              "MM/DD/YYYY"
                            )}
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            <strong>{lang.totalALSFRSRAssessments || "Total ALSFRS-R Assessments:"}</strong> 0
                          </p>
                          <p style={{ color: "#999", fontStyle: "italic" }}>
                            {lang.noAssessmentDataAvailable || "No assessment data available yet."}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Column 3: Speech Sessions */}
                    <div style={{ flex: 1 }}>
                      {speechData.length > 0 ? (
                        <>
                          <p>
                            <strong>{lang.totalSpeechSessions || "Total Speech Sessions:"}</strong>{" "}
                            {speechData.length}
                          </p>
                          <p>
                            <strong>{lang.latestSpeechSession || "Latest Speech Session:"}</strong>{" "}
                            {moment(speechData[0]?.date_created).format(
                              "MM/DD/YYYY"
                            )}
                          </p>
                        </>
                      ) : (
                        <>
                          <p>
                            <strong>{lang.totalSpeechSessions || "Total Speech Sessions:"}</strong> 0
                          </p>
                          <p style={{ color: "#999", fontStyle: "italic" }}>
                            {lang.noSpeechSessionDataAvailable || "No speech session data available yet."}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </section>

              {/* ALSFRS-R Scores */}
              {alsfrsrData.length > 0 && (
                <section className="report-section page-break-before">
                  <h2 className="section-title">{lang.alsfrsrScores || "ALSFRS-R Scores"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.date || "Date"}</th>
                          <th>{lang.speech || "Speech"}</th>
                          <th>{lang.saliv || "Saliv."}</th>
                          <th>{lang.swall || "Swall."}</th>
                          <th>{lang.hand || "Hand."}</th>
                          <th>{lang.cutFood || "Cut Food"}</th>
                          <th>{lang.dress || "Dress."}</th>
                          <th>{lang.turn || "Turn"}</th>
                          <th>{lang.walk || "Walk"}</th>
                          <th>{lang.stairs || "Stairs"}</th>
                          <th>{lang.dysp || "Dysp."}</th>
                          <th>{lang.ortho || "Ortho."}</th>
                          <th>{lang.respInsuf || "Resp. Insuf."}</th>
                          <th>{lang.total || "Total"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alsfrsrData
                          .sort(
                            (a, b) =>
                              new Date(b.created_at) - new Date(a.created_at)
                          )
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                {moment(item.created_at).format("MM/DD/YYYY")}
                              </td>
                              <td>{item.speech || "---"}</td>
                              <td>{item.salvation || "---"}</td>
                              <td>{item.swallowing || "---"}</td>
                              <td>{item.handwriting || "---"}</td>
                              <td>
                                {item.cutting_food_a ||
                                  item.cutting_food_b ||
                                  "---"}
                              </td>
                              <td>{item.dressing_and_hygiene || "---"}</td>
                              <td>{item.turning_in_bed || "---"}</td>
                              <td>{item.walking || "---"}</td>
                              <td>{item.climbing_stairs || "---"}</td>
                              <td>{item.dyspnea || "---"}</td>
                              <td>{item.orthopnea || "---"}</td>
                              <td>{item.respiratory_insufficiency || "---"}</td>
                              <td>
                                <strong>{item.ALS_total || "---"}</strong>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* FVC Data */}
              {respiratoryData.length > 0 && (
                <section className="report-section page-break-before fvc-table-section">
                  <h2 className="section-title">{lang.forcedVitalCapacity || "Forced Vital Capacity (FVC)"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th rowSpan="2">{lang.date || "Date"}</th>
                          <th colSpan="3">{lang.fvc1 || "FVC 1"}</th>
                          <th colSpan="3">{lang.fvc2 || "FVC 2"}</th>
                          <th colSpan="3">{lang.fvc3 || "FVC 3"}</th>
                          <th colSpan="3">{lang.greatest || "Greatest"}</th>
                        </tr>
                        <tr>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                          <th>{lang.percentP || "%P"}</th>
                          <th>{lang.ml || "mL"}</th>
                          <th>{lang.l || "L"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respiratoryData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                {moment(item.entry_date).format("MM/DD/YYYY")}
                              </td>
                              <td>{item.fvc1 || "---"}</td>
                              <td>{item.fvc1Raw || "---"}</td>
                              <td>{item.fvc1RawL || "---"}</td>
                              <td>{item.fvc2 || "---"}</td>
                              <td>{item.fvc2Raw || "---"}</td>
                              <td>{item.fvc2RawL || "---"}</td>
                              <td>{item.fvc3 || "---"}</td>
                              <td>{item.fvc3Raw || "---"}</td>
                              <td>{item.fvc3RawL || "---"}</td>
                              <td>
                                <strong>{item.greatestFVC || "---"}</strong>
                              </td>
                              <td>
                                <strong>{item.greatestFVCRaw || "---"}</strong>
                              </td>
                              <td>
                                <strong>{item.greatestFVCRawL || "---"}</strong>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* PCF Data */}
              {respiratoryData.length > 0 && (
                <section className="report-section page-break-before">
                  <h2 className="section-title">{lang.peakCoughFlow || "Peak Cough Flow (PCF)"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.pcf1 || "PCF 1 (L/min)"}</th>
                          <th>{lang.pcf2 || "PCF 2 (L/min)"}</th>
                          <th>{lang.pcf3 || "PCF 3 (L/min)"}</th>
                          <th>{lang.greatestPCF || "Greatest PCF (L/min)"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {respiratoryData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => (
                            <tr key={index}>
                              <td>
                                {moment(item.entry_date).format("MM/DD/YYYY")}
                              </td>
                              <td>{item.pcf1 || "---"}</td>
                              <td>{item.pcf2 || "---"}</td>
                              <td>{item.pcf3 || "---"}</td>
                              <td>
                                <strong>{item.greatestPCF || "---"}</strong>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* MPT Data */}
              {mptData.length > 0 && (
                <section className="report-section page-break-before">
                  <h2 className="section-title">
                    {lang.maximumPhonationTime || "Maximum Phonation Time (MPT)"}
                  </h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.mpt1 || "MPT 1 (seconds)"}</th>
                          <th>{lang.mpt2 || "MPT 2 (seconds)"}</th>
                          <th>{lang.mpt3 || "MPT 3 (seconds)"}</th>
                          <th>{lang.greatestMPT || "Greatest MPT (seconds)"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mptData
                          .sort(
                            (a, b) =>
                              new Date(b.entry_date) - new Date(a.entry_date)
                          )
                          .map((item, index) => {
                            // Calculate greatest MPT from mpt1, mpt2, mpt3
                            const mptValues = [
                              item.mpt1 !== null && item.mpt1 !== undefined
                                ? item.mpt1
                                : null,
                              item.mpt2 !== null && item.mpt2 !== undefined
                                ? item.mpt2
                                : null,
                              item.mpt3 !== null && item.mpt3 !== undefined
                                ? item.mpt3
                                : null,
                            ].filter((val) => val !== null);
                            const greatestMPT =
                              mptValues.length > 0
                                ? Math.max(...mptValues)
                                : null;

                            return (
                              <tr key={index}>
                                <td>
                                  {moment(item.entry_date).format("MM/DD/YYYY")}
                                </td>
                                <td>
                                  {item.mpt1 !== null && item.mpt1 !== undefined
                                    ? item.mpt1
                                    : "---"}
                                </td>
                                <td>
                                  {item.mpt2 !== null && item.mpt2 !== undefined
                                    ? item.mpt2
                                    : "---"}
                                </td>
                                <td>
                                  {item.mpt3 !== null && item.mpt3 !== undefined
                                    ? item.mpt3
                                    : "---"}
                                </td>
                                <td>
                                  <strong>
                                    {greatestMPT !== null ? greatestMPT : "---"}
                                  </strong>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Speech Sessions Data */}
              {speechData && speechData.length > 0 && (
                <section className="report-section page-break-before">
                  <h2 className="section-title">{lang.speechSessions || "Speech Sessions"}</h2>
                  <div className="section-content">
                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>{lang.sessionDate || "Session Date"}</th>
                          <th>{lang.speakingRate || "Speaking Rate"}</th>
                          <th>{lang.sessionDuration || "Session Duration"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {speechData
                          .filter((item) => item && item.date_created)
                          .sort((a, b) => {
                            try {
                              const dateA = a.date_created
                                ? moment(a.date_created)
                                : moment(0);
                              const dateB = b.date_created
                                ? moment(b.date_created)
                                : moment(0);
                              return dateB - dateA;
                            } catch (e) {
                              return 0;
                            }
                          })
                          .map((item, index) => {
                            let sessionDate = "---";
                            try {
                              if (item.date_created) {
                                sessionDate = moment(item.date_created).format(
                                  "MM/DD/YYYY"
                                );
                              }
                            } catch (e) {
                              sessionDate = "---";
                            }
                            return (
                              <tr key={`speech-${item.session_id || index}`}>
                                <td>{sessionDate}</td>
                                <td>
                                  {item.speaking_rate !== null &&
                                  item.speaking_rate !== undefined &&
                                  item.speaking_rate !== ""
                                    ? item.speaking_rate
                                    : "---"}
                                </td>
                                <td>
                                  {item.total_duration || "---"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Footer for printed page */}
              <div className="print-only print-page-footer">
                <hr />
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#666",
                  }}
                >
                  {lang.footerText || "Generated from EverythingALS Patient Portal -"}{" "}
                  {moment().format("MMMM DD, YYYY")}
                </p>
              </div>
            </div>
          )}
        </Spin>
      </div>
    );
  }
}
